// Tiny DOM helpers and formatting — no framework needed for an app this size.

type Child = Node | string | number | null | undefined | false;
type Attrs = Record<string, unknown> & { class?: string; onclick?: (e: MouseEvent) => void };

/**
 * Event handlers live in a per-element table, called through one shared listener. That lets a screen refresh hand
 * the newest handlers to an element that stays on screen (the box you're typing in, see mount()).
 */
type Handlers = Record<string, (e: Event) => void>;
const HANDLERS = Symbol("handlers");
function dispatch(this: Element, e: Event) {
  (this as unknown as { [HANDLERS]?: Handlers })[HANDLERS]?.[e.type]?.call(this, e);
}
function setHandlers(el: Element, handlers: Handlers) {
  (el as unknown as { [HANDLERS]?: Handlers })[HANDLERS] = handlers;
  for (const type of Object.keys(handlers)) el.addEventListener(type, dispatch); // same listener twice = no-op
}
const handlersOf = (el: Element): Handlers => (el as unknown as { [HANDLERS]?: Handlers })[HANDLERS] ?? {};

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k.startsWith("on") && typeof v === "function") setHandlers(el, { ...handlersOf(el), [k.slice(2)]: v as (e: Event) => void });
    else if (k === "class") el.className = String(v);
    else if (k in el && k !== "list") (el as unknown as Record<string, unknown>)[k] = v;
    else el.setAttribute(k, String(v));
  }
  for (const c of children) if (c !== null && c !== undefined && c !== false) el.append(c instanceof Node ? c : String(c));
  return el;
}

const TEXT_TYPES = new Set(["", "text", "search", "email", "tel", "url", "password", "number"]);
export const isTextField = (el: Element | null): el is HTMLInputElement | HTMLTextAreaElement =>
  !!el && (el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && TEXT_TYPES.has(el.getAttribute("type") ?? "")));

/** Fields whose typed text should survive a refresh even if the fresh copy says otherwise (see main.ts). */
let keepTyped: (id: string) => boolean = () => false;
export function setKeepTyped(fn: (id: string) => boolean) {
  keepTyped = fn;
}

/** Copies attributes and handlers from the fresh copy onto the element that stays on screen. */
/** Gives a kept element on screen the fresh copy's attributes and handlers (its children are the caller's job). */
export function adoptInto(keep: Element, fresh: Element) {
  adopt(keep, fresh);
}
function adopt(keep: Element, fresh: Element, isField = false) {
  // The field keeps its own inline size (a message box grows with what you've typed).
  const skip = (name: string) => isField && name === "style";
  for (const a of [...keep.attributes]) if (!fresh.hasAttribute(a.name) && !skip(a.name)) keep.removeAttribute(a.name);
  for (const a of [...fresh.attributes]) if (keep.getAttribute(a.name) !== a.value && !skip(a.name)) keep.setAttribute(a.name, a.value);
  setHandlers(keep, handlersOf(fresh));
}

/**
 * The fresh copy of a kept field is never shown, but code may still hold it (forms read `email.value` on submit,
 * the keypad writes `display.value`): reads and writes on it go to the field on screen.
 */
function forward(fresh: HTMLInputElement | HTMLTextAreaElement, keep: HTMLInputElement | HTMLTextAreaElement) {
  const props = ["value", "selectionStart", "selectionEnd", "disabled", "placeholder", "readOnly"] as const;
  for (const prop of props) {
    try {
      Object.defineProperty(fresh, prop, { configurable: true, get: () => keep[prop], set: (v) => ((keep as unknown as Record<string, unknown>)[prop] = v) });
    } catch {
      // not redefinable here
    }
  }
  for (const fn of ["focus", "blur", "select", "setSelectionRange", "setRangeText", "checkValidity", "reportValidity", "dispatchEvent", "getBoundingClientRect", "scrollIntoView"] as const) {
    const real = (keep as unknown as Record<string, unknown>)[fn];
    if (typeof real === "function") Object.defineProperty(fresh, fn, { configurable: true, value: (...args: unknown[]) => (real as (...a: unknown[]) => unknown).apply(keep, args) });
  }
  for (const prop of ["style", "classList", "dataset", "isConnected", "parentElement", "scrollHeight", "validity"] as const) {
    Object.defineProperty(fresh, prop, { configurable: true, get: () => (keep as unknown as Record<string, unknown>)[prop] });
  }
}

/**
 * Shows a freshly built screen. Normally a plain swap; but while you're typing in a field, that field and the
 * elements around it stay on screen (only what's around them is replaced). Swapping out the field you're typing
 * in is what made typing jumpy on iPhone: the keyboard, cursor and autocorrect restarted on every refresh.
 */
export function mount(root: HTMLElement, ...nodes: Node[]) {
  if (mountSteady(root, nodes)) return;
  const active = document.activeElement;
  if (isTextField(active) && active.id && root.contains(active)) {
    const top = [...root.children].find((c) => c.contains(active));
    const freshTop = nodes.find((n): n is Element => n instanceof Element && (n.id === active.id || !!n.querySelector(`#${CSS.escape(active.id)}`)));
    if (top && freshTop && keepChain(top, freshTop, active)) {
      placeAround(root, top, nodes, freshTop);
      return;
    }
  }
  const sc = recentScroller();
  if (sc && root.contains(sc)) {
    const top = [...root.children].find((c) => c.contains(sc));
    const freshTop = nodes.find((n): n is Element => n instanceof Element && !!findScroller(n, scrollKey(sc)!));
    if (top && freshTop && keepScroller(top, freshTop, sc)) {
      placeAround(root, top, nodes, freshTop);
      return;
    }
  }
  root.replaceChildren(...nodes);
}

// ---- 4.10.29: lists stay on screen; rows that didn't change aren't touched ----
// "The whole app lags": every refresh (a tap, a new text, a teammate's chat message) built the whole screen again
// and swapped it in, so the phone restyled, laid out and repainted every inbox row and every message bubble for one
// new badge or one new message (measured: most of each refresh's time). Now, on a refresh of the same screen, the
// inbox list and the open conversation / chat stay on screen, and inside them each row whose content is exactly the
// same stays where it is, untouched (it takes the fresh copy's handlers, so a tap does the newest thing). Only new
// or changed rows go in. A different screen (another page, another conversation) is still drawn fresh.

let sameScreen = false;
/** Set by the app around a redraw: true when it redraws the screen that's showing (same page, same conversation). */
export function setSameScreen(v: boolean) {
  sameScreen = v;
}
/** Lists whose rows are kept (by data-scroll). */
const STEADY_LISTS = ["list", "timeline"];
/** A row that can stay: an inbox row (by its number) or a message bubble (by its id), with no form fields in it. */
function rowKey(n: Node): string | null {
  if (!(n instanceof Element)) return null;
  if (n.classList.contains("swipe-row")) return n.getAttribute("data-number") ? `n:${n.getAttribute("data-number")}` : null;
  if (n.classList.contains("bubble-row") && n.id) return `i:${n.id}`;
  return null;
}
const LIVE_INSIDE = "input, textarea, select, audio, video, canvas, iframe";

/** `parent`'s children become `kids`, in order, moving as little as possible (children already in place stay put). */
function setKids(parent: Element, kids: Node[]) {
  const want = new Set(kids);
  for (const c of [...parent.childNodes]) if (!want.has(c)) c.remove();
  let cur: ChildNode | null = parent.firstChild;
  for (const n of kids) {
    if (n === cur) {
      cur = cur.nextSibling;
      continue;
    }
    parent.insertBefore(n, cur);
  }
}

/** A list that stays: its unchanged rows stay exactly where they are; new or changed ones come from `fresh`. */
function reconcileRows(old: Element, fresh: Element, depth = 0) {
  adopt(old, fresh);
  // One wrapper inside (the conversation's inner column): it stays too, and its rows are matched.
  if (depth < 2 && fresh.childNodes.length === 1 && old.childNodes.length === 1) {
    const o = old.firstElementChild;
    const f = fresh.firstElementChild;
    if (o && f && o.tagName === f.tagName && o.className === f.className && !rowKey(f)) return reconcileRows(o, f, depth + 1);
  }
  const onScreen = new Map<string, Element | null>();
  for (const c of old.children) {
    const k = rowKey(c);
    if (k) onScreen.set(k, onScreen.has(k) ? null : c); // the same key twice: neither is reused
  }
  const active = document.activeElement;
  const kids = [...fresh.childNodes].map((f) => {
    const k = rowKey(f);
    const o = k ? onScreen.get(k) : null;
    if (!o || !(f instanceof Element) || (active && o.contains(active)) || f.querySelector(LIVE_INSIDE) || !o.isEqualNode(f)) return f;
    const a = [o, ...o.querySelectorAll("*")];
    const b = [f, ...f.querySelectorAll("*")];
    if (a.length !== b.length) return f;
    for (let i = 0; i < a.length; i++) setHandlers(a[i], handlersOf(b[i]));
    onScreen.delete(k!);
    return o;
  });
  setKids(old, kids);
}

/**
 * A redraw of the same screen: the lists (and the field you're typing in, if any) stay on screen with every
 * element above them; everything else is the fresh copy. False when the fresh screen doesn't have the same shape
 * (then it's drawn fresh as before).
 */
function mountSteady(root: Element, nodes: Node[], onlyTop?: [Element, Element]): boolean {
  if (!sameScreen) return false;
  const active = document.activeElement;
  const field = isTextField(active) && active.id && root.contains(active) ? active : null;
  const scope = onlyTop ? [onlyTop[1]] : nodes;
  const findFresh = (pick: (top: Element) => Element | null) => {
    for (const n of scope) if (n instanceof Element) {
      const hit = pick(n);
      if (hit) return hit;
    }
    return null;
  };
  const pairs: { o: Element; f: Element; kind: "rows" | "field" }[] = [];
  for (const key of STEADY_LISTS) {
    const sc = (onlyTop ? onlyTop[0] : root).querySelector(`[data-scroll="${key}"]`);
    if (!sc || (field && sc.contains(field))) continue;
    const f = findFresh((t) => findScroller(t, `s:${key}`));
    if (f && f.tagName === sc.tagName) pairs.push({ o: sc, f, kind: "rows" });
  }
  if (!pairs.length) return false;
  // Scrolling something else right now (a side panel, a page): the usual way keeps that one under your finger.
  const sc = recentScroller();
  if (sc && (onlyTop ? onlyTop[0] : root).contains(sc) && !pairs.some((p) => p.o === sc || p.o.contains(sc))) return false;
  if (field) {
    const f = findFresh((t) => (t.id === field.id ? t : t.querySelector(`#${CSS.escape(field.id)}`)));
    if (!f || f.tagName !== field.tagName || (f.getAttribute("type") ?? "") !== (field.getAttribute("type") ?? "")) return false;
    pairs.push({ o: field, f, kind: "field" });
  }
  // Each fresh element on the way down to a kept one ↔ the one on screen in its place (same tags all the way).
  const map = new Map<Element, Element>();
  const top = onlyTop ? onlyTop[0] : null;
  for (const { o, f } of pairs) {
    const oc: Element[] = [];
    for (let el: Element | null = o; el && el !== root; el = el === top ? null : el.parentElement) oc.unshift(el);
    const fc: Element[] = [];
    for (let el: Element | null = f; el; el = el.parentElement) fc.unshift(el);
    if (oc.length !== fc.length || !scope.includes(fc[0]) || (top && oc[0] !== top)) return false;
    for (let i = 0; i < oc.length; i++) {
      if (oc[i].tagName !== fc[i].tagName) return false;
      const was = map.get(fc[i]);
      if (was && was !== oc[i]) return false;
      map.set(fc[i], oc[i]);
    }
  }
  if (new Set(map.values()).size !== map.size) return false;
  const kind = new Map(pairs.map((p) => [p.f, p.kind]));
  const apply = (f: Element) => {
    const o = map.get(f)!;
    const k = kind.get(f);
    if (k === "rows") return reconcileRows(o, f);
    if (k === "field") {
      adopt(o, f, true);
      const ff = f as HTMLInputElement | HTMLTextAreaElement;
      const oo = o as HTMLInputElement | HTMLTextAreaElement;
      const want = ff.value;
      if (!keepTyped(oo.id) && oo.value !== want) oo.value = want;
      forward(ff, oo);
      return;
    }
    adopt(o, f);
    const kids = [...f.childNodes];
    for (const c of kids) if (c instanceof Element && map.has(c)) apply(c);
    setKids(o, kids.map((c) => (c instanceof Element && map.get(c)) || c));
  };
  const kids = scope.map((n) => (n instanceof Element && map.get(n)) || n);
  for (const n of scope) if (n instanceof Element && map.has(n)) apply(n);
  if (!onlyTop) setKids(root, kids);
  return true;
}

// ---- The list you're scrolling stays put ----
// On a phone, a scroll belongs to the element your finger started on. Screen refreshes used to swap the inbox list
// for a fresh copy, which left your drag holding an element no longer on screen: the inbox "stuck" for the first
// few seconds after opening the app (while the inbox, team, statuses and phone line all arrive and refresh it).
// Now the element you're scrolling stays on screen and only what's inside it is replaced.

const scrollKey = (el: Element): string | null =>
  (el as HTMLElement).dataset?.scroll ? `s:${(el as HTMLElement).dataset.scroll}` : el.classList?.contains("page") ? "page" : null;
function findScroller(top: Element, key: string): Element | null {
  if (scrollKey(top) === key) return top;
  return key.startsWith("s:") ? top.querySelector(`[data-scroll="${CSS.escape(key.slice(2))}"]`) : top.querySelector(".page");
}
let lastScroller: Element | null = null;
let lastScrollAt = 0;
/** Fingers on the screen right now (from touch events: a scroll cancels the pointer events as soon as it starts). */
let fingers = 0;
export const fingersOnScreen = () => fingers;
if (typeof document !== "undefined") {
  const note = (e: Event) => {
    const t = e.target as Element | null;
    const sc = t && "closest" in t ? t.closest("[data-scroll], .page") : null;
    if (sc) {
      lastScroller = sc;
      lastScrollAt = Date.now();
    }
  };
  for (const type of ["touchstart", "pointerdown", "wheel"]) document.addEventListener(type, note, { capture: true, passive: true });
  const count = (e: Event) => (fingers = (e as TouchEvent).touches.length);
  document.addEventListener("touchstart", (e) => {
    count(e);
    // A refresh can take the touched element off the page mid-scroll: its touchend then never reaches the document.
    for (const type of ["touchend", "touchcancel"]) e.target?.addEventListener(type, count, { passive: true, once: true });
  }, { capture: true, passive: true });
  for (const type of ["touchend", "touchcancel"]) document.addEventListener(type, count, { capture: true, passive: true });
  document.addEventListener("scroll", note, { capture: true, passive: true });
}
/** The list being touched or scrolled (in the last few seconds, momentum included), if it's still on screen. */
function recentScroller(): Element | null {
  return lastScroller && lastScroller.isConnected && (fingers > 0 || Date.now() - lastScrollAt < 4000) ? lastScroller : null;
}

/** Like keepChain, for a scrolling list: the chain down to it stays; the list's contents are the fresh ones. */
function keepScroller(oldTop: Element, freshTop: Element, sc: Element): boolean {
  const key = scrollKey(sc);
  const fresh = key ? findScroller(freshTop, key) : null;
  if (!fresh || fresh.tagName !== sc.tagName) return false;
  const oldChain: Element[] = [];
  for (let el: Element | null = sc; el; el = el === oldTop ? null : el.parentElement) oldChain.unshift(el);
  const freshChain: Element[] = [];
  for (let el: Element | null = fresh; el; el = el === freshTop ? null : el.parentElement) freshChain.unshift(el);
  if (oldChain[0] !== oldTop || freshChain[0] !== freshTop || oldChain.length !== freshChain.length) return false;
  for (let i = 0; i < oldChain.length; i++) if (oldChain[i].tagName !== freshChain[i].tagName) return false;
  for (let i = 0; i < oldChain.length - 1; i++) {
    adopt(oldChain[i], freshChain[i]);
    placeAround(oldChain[i], oldChain[i + 1], [...freshChain[i].childNodes], freshChain[i + 1]);
  }
  adopt(sc, fresh);
  sc.replaceChildren(...fresh.childNodes);
  return true;
}

/**
 * A frosted call window updated in place: `old` (on screen) and its frosted card (`frameSel`, a direct child) stay,
 * taking the fresh attributes and handlers, and everything inside comes from `fresh`. Pieces that didn't change
 * (`keepSel`, e.g. the photo and its ringing ripples) aren't swapped either. iPhone draws a freshly made frosted
 * panel unblurred for a moment and restarts the ripples, so rebuilding the window on each update made it flash.
 * False when the two don't have the same shape (then rebuild as usual).
 */
export function reuseFrame(old: Element, fresh: Element, frameSel: string, keepSel: string): boolean {
  const oFrame = [...old.children].find((c) => c.matches(frameSel));
  const fFrame = [...fresh.children].find((c) => c.matches(frameSel));
  if (!oFrame || !fFrame || old.tagName !== fresh.tagName || oFrame.tagName !== fFrame.tagName) return false;
  const oKeep = oFrame.querySelector(keepSel);
  const fKeep = fFrame.querySelector(keepSel);
  if (oKeep && fKeep && oKeep.outerHTML === fKeep.outerHTML) fKeep.replaceWith(oKeep);
  adopt(old, fresh);
  adopt(oFrame, fFrame);
  oFrame.replaceChildren(...fFrame.childNodes);
  old.replaceChildren(...[...fresh.childNodes].map((n) => (n === fFrame ? oFrame : n)));
  return true;
}

/** Refreshes one part of the screen (`old` becomes `fresh`), keeping the field you're typing in the same way. */
export function patch(old: Element, fresh: Element) {
  if (old.tagName === fresh.tagName && old.parentElement && mountSteady(old.parentElement, [fresh], [old, fresh])) return;
  const active = document.activeElement;
  if (isTextField(active) && active.id && old.contains(active) && keepChain(old, fresh, active)) return;
  const sc = recentScroller();
  if (sc && old.contains(sc) && keepScroller(old, fresh, sc)) return;
  old.replaceWith(fresh);
}

/** Replaces the children of `parent` around `kept`, never moving `kept` itself (moving it would blur the field). */
function placeAround(parent: Element, kept: Element, freshKids: Node[], freshKept: Element) {
  const at = freshKids.indexOf(freshKept);
  for (const c of [...parent.childNodes]) if (c !== kept) c.remove();
  for (const n of freshKids.slice(0, at)) parent.insertBefore(n, kept);
  kept.after(...freshKids.slice(at + 1));
}

/**
 * `oldTop` (on screen, containing the field you're typing in) takes the place of `freshTop`: every element from
 * it down to the field stays on screen and takes the fresh attributes and handlers; everything else is the fresh
 * copy. False (nothing changed) when the fresh screen has no matching field at the same place.
 */
function keepChain(oldTop: Element, freshTop: Element, active: HTMLInputElement | HTMLTextAreaElement): boolean {
  const fresh = freshTop.id === active.id ? freshTop : freshTop.querySelector(`#${CSS.escape(active.id)}`);
  if (!fresh || fresh.tagName !== active.tagName || (fresh.getAttribute("type") ?? "") !== (active.getAttribute("type") ?? "")) return false;
  const oldChain: Element[] = [];
  for (let el: Element | null = active; el; el = el === oldTop ? null : el.parentElement) oldChain.unshift(el);
  const freshChain: Element[] = [];
  for (let el: Element | null = fresh; el; el = el === freshTop ? null : el.parentElement) freshChain.unshift(el);
  if (oldChain[0] !== oldTop || freshChain[0] !== freshTop || oldChain.length !== freshChain.length) return false;
  for (let i = 0; i < oldChain.length; i++) if (oldChain[i].tagName !== freshChain[i].tagName) return false;
  for (let i = 0; i < oldChain.length; i++) {
    adopt(oldChain[i], freshChain[i], i === oldChain.length - 1);
    if (i < oldChain.length - 1) placeAround(oldChain[i], oldChain[i + 1], [...freshChain[i].childNodes], freshChain[i + 1]);
  }
  // The field: newest value unless it's your unsaved typing; the fresh copy forwards to it from now on.
  const f = fresh as HTMLInputElement | HTMLTextAreaElement;
  const want = f.value;
  if (!keepTyped(active.id) && active.value !== want) active.value = want;
  forward(f, active);
  return true;
}

/** Server timestamps are UTC without a zone ("2026-10-08 15:12:50"). */
export function serverTime(s: string | null): Date | null {
  return s ? new Date(s.replace(" ", "T") + "Z") : null;
}

export function timeAgo(s: string | null): string {
  const d = serverTime(s);
  if (!d) return "";
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return fmtDate(d, { month: "short", day: "numeric" });
}

/**
 * Time stamp for inbox rows and lists, always with the time of day: "1:34 PM" today, "Fri 4:10 PM" from
 * yesterday through the past week, "Oct 2, 4:10 PM" this year, "Oct 2, 2025" before that.
 */
/**
 * Date formatters made once: toLocaleString(…, options) builds a new formatter on every call, which was most of the
 * time spent drawing the inbox (two per row) at phone speed.
 */
const fmtCache = new Map<string, Intl.DateTimeFormat>();
export function fmtDate(d: Date, o: Intl.DateTimeFormatOptions): string {
  const key = JSON.stringify(o);
  let f = fmtCache.get(key);
  if (!f) fmtCache.set(key, (f = new Intl.DateTimeFormat(undefined, o)));
  return f.format(d);
}

export function listTime(s: string | null, now = new Date()): string {
  const d = serverTime(s);
  if (!d) return "";
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86_400_000);
  const t = fmtDate(d, { hour: "numeric", minute: "2-digit" });
  if (days <= 0) return t;
  if (days < 7) return `${fmtDate(d, { weekday: "short" })} ${t}`;
  if (d.getFullYear() === now.getFullYear()) return `${fmtDate(d, { month: "short", day: "numeric" })}, ${t}`;
  return fmtDate(d, { month: "short", day: "numeric", year: "numeric" });
}

/** Full date and time, for tooltips: "Thu, Oct 9, 1:34 PM". */
export function fullTime(s: string | null): string {
  const d = serverTime(s);
  return d ? fmtDate(d, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
}

export function when(s: string | null): string {
  const d = serverTime(s);
  return d ? fmtDate(d, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
}

export function duration(secs: number | null): string {
  if (secs == null || !Number.isFinite(secs)) return "";
  const m = Math.floor(secs / 60);
  return `${m}:${String(Math.floor(secs % 60)).padStart(2, "0")}`;
}

/** "+14155550123" → "(415) 555-0123"; other numbers stay as they are. */
export function phone(n: string): string {
  if (n.startsWith("grp:")) return n.slice(4).split(",").map(phone).join(", "); // a group text's members
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(n);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : n;
}

/** Turns what was typed into +E164. Ten digits are treated as a US/Canada number. */
/** "+1 800 555 1234 x22" → the number and the extension (same rules as the server's splitExtension). */
export function splitExtension(raw: string): { number: string; ext: string | null } {
  const s = String(raw ?? "").trim();
  const m = /^(.*?\d[\d\s().+-]*?)\s*(?:(?:,|;\s*ext\s*=|\b(?:extension|ext|x)\.?\s*:?(?=\s*\d)|#)\s*)+(\d{1,8})\s*$/i.exec(s);
  // Only a full number before it counts ("1,800,555,1234" is one number written with commas, not an extension).
  const full = m ? m[1].replace(/\D/g, "").length >= (m[1].trim().startsWith("+") ? 8 : 10) : false;
  return m && full ? { number: m[1].trim(), ext: m[2] } : { number: s, ext: null };
}

export function toE164(input: string): string | null {
  // An extension ("x123", "ext. 45", ",,123") is never part of the number.
  const cleaned = splitExtension(input).number.replace(/[^\d+]/g, "");
  if (/^\+\d{8,15}$/.test(cleaned)) return cleaned;
  const digits = cleaned.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

export function statusText(status: string, direction: string): string {
  switch (status) {
    case "ringing": return "Ringing";
    case "active": return "On a call";
    case "missed": return direction === "outbound" ? "No answer" : "Missed";
    case "voicemail": return "Voicemail";
    default: return direction === "outbound" ? "Outgoing" : "Incoming";
  }
}

let toastTimer: number | undefined;
export function toast(text: string) {
  document.querySelector(".toast")?.remove();
  const el = h("div", { class: "toast", role: "status" }, text);
  document.body.append(el);
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => el.remove(), 3500);
}

/** The ringtone player now lives in sounds.ts (choosable ringtones, volume, ring speaker); re-exported here. */
export { Ringer } from "./sounds";
